import React, {Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef} from "react";
import LeaderboardRowDivider from "./LeaderboardRowDivider";
import LeaderboardRow, {LeaderboardRowProps} from "./LeaderboardRow";
import {EngageAppContext} from "../../App";
import {Theme} from "engage-client/theme";
import {createUseStyles, useTheme} from "react-jss";
import {LeaderboardEntry, UserScores, UserState} from "../../state/state";
import {useDimensionObserver} from "engage-client/hooks/useDimensionObserver";

import {
	AccessoryLgWidth,
	AccessoryXlWidth,
	AccessoryXxlWidthProportion,
	LeaderboardFinalRowGap,
	LeaderboardRowGap,
	LeaderboardRowHeight,
	LeaderboardRowHeightXxlProportion,
	LeaderboardRowHeightCompact,
	LeaderboardRowHeightCompactXxlProportion,
	LeaderboardStartRowY,
	LeaderboardStartRowYXxlProportion
} from "../accessory/accessoryConstants";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import {Role, Team, TeamMode, User} from "engage-common/types";
import LeaderboardTitleRow from "./LeaderboardTitleRow";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {useWindowSize} from "engage-client/hooks/useResizeObserver";

type LeaderboardProps = {
	setHoveredUserID?: Dispatch<SetStateAction<string|undefined>>
	setLeaderboardRowCount?: Dispatch<SetStateAction<number>>
	hoveredUserID?: string
}

export default function Leaderboard(props:LeaderboardProps) {
	const {appState, userState, user, lobby} = useContext(EngageAppContext.current);
	const scores = appState.extra?.leaderboard ?? [];
	const teamScores = appState.extra?.teamScores ?? {};
	const teamMode = lobby?.teamMode !== TeamMode.Off;
	const teams = lobby?.teams ?? [];
	const showAllRankChanges = (user && user.role === Role.host) ?? false;

	return <PureLeaderboard leaderboard={scores} userState={userState} user={user}
							showAllRankChanges={showAllRankChanges} setHoveredUserID={props.setHoveredUserID}
							setLeaderboardRowCount={props.setLeaderboardRowCount}
							teamMode={teamMode} teamScores={teamScores} teams={teams}/>;
};

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	leaderboard: {
		position: "relative",
		overflow: "hidden",
		[theme.breakpoints.only("lg")] : {
			width: AccessoryLgWidth,
			maxWidth: AccessoryLgWidth,
		},
		[theme.breakpoints.up("xl")] : {
			width: AccessoryXlWidth,
			maxWidth: AccessoryXlWidth,
		},
		[theme.breakpoints.up("xxl")] : {
			width: `${AccessoryXxlWidthProportion}vw`,
			maxWidth: `${AccessoryXxlWidthProportion}vw`,
		},
		[theme.breakpoints.down("md")]: {
			width: "calc(100% - 40px)",
		},
		flexDirection:"column",
		display: "flex",
		flex: 1,
	},
	titles: {
		color: theme.accessoryPanel.textColor,
		display: "flex",
		justifyContent: "space-between",
		marginRight: 60
	},
	rowContainer: {
		flex:1
	},
	rowDivider : {
		position: "absolute",
		top: 0,
		right: 0,
	}
}));

type PureLeaderboardProps = LeaderboardProps & {
	leaderboard: readonly LeaderboardEntry[]
	userState: UserState
	user?: User
	userRow?: boolean
	showAllRankChanges: boolean
	teamMode: boolean
	teams: Team[]
	teamScores: {readonly [teamID:string]:UserScores}
};

export function PureLeaderboard(props:PureLeaderboardProps) {
	const {leaderboard, showAllRankChanges, userState, user, userRow = true, setHoveredUserID, setLeaderboardRowCount} = props;
	const theme = useTheme<Theme>();
	const isXxl = useMediaQuery(theme.breakpoints.up("xxl"));
	const containerRef = useRef(null);
	const {width:containerWidth,height:containerHeight} = useDimensionObserver(containerRef);
	const windowWidth = useWindowSize().width;
	let leaderboardRowHeightXxl = -1;
	let leaderboardRowHeightCompactXxl = -1;
	let leaderboardStartRowYXxl = -1;
    if (windowWidth) {
        leaderboardRowHeightXxl = (LeaderboardRowHeightXxlProportion * windowWidth) / 100;
		leaderboardRowHeightCompactXxl = (LeaderboardRowHeightCompactXxlProportion * windowWidth) / 100;
		leaderboardStartRowYXxl = (LeaderboardStartRowYXxlProportion * windowWidth) / 100;
    }
	const classes = useStyles();
	const compact = useMediaQuery(theme.breakpoints.only("xs"));
	const totalRowHeight = (compact ? isXxl ? leaderboardRowHeightCompactXxl : LeaderboardRowHeightCompact : isXxl ? leaderboardRowHeightXxl : LeaderboardRowHeight) + LeaderboardRowGap;
	let rowLimit = Math.floor(((containerHeight ?? 0) - (isXxl ? leaderboardStartRowYXxl : LeaderboardStartRowY)) / totalRowHeight);
	let lastPosition = 0;
	const userIndex = leaderboard.findIndex(value => value.userID === user?.id);
	const teamRows = props.teamMode ? props.teams.length + 2 : 0;

	let userRowAfter = userRow && user && (userIndex >= (rowLimit - teamRows) || (userState.position && userIndex === -1));
	if(userRowAfter) {
		//Recalculate in case the extra gap means we need 1 fewer row
		rowLimit = Math.floor(((containerHeight ?? 0) - ((isXxl ? leaderboardStartRowYXxl : LeaderboardStartRowY) + LeaderboardFinalRowGap)) / totalRowHeight);
		//Remove the bottom row either way
		rowLimit--;
	}
	useEffect(() => {
		if(setLeaderboardRowCount) {
			setLeaderboardRowCount(rowLimit);
		}
	}, [setLeaderboardRowCount, rowLimit]);


	const onMouseEnter = (userID:string) => {
		if(setHoveredUserID) {
			setHoveredUserID(userID);
		}
	};

	const onMouseLeave = (userID:string) => {
		if(setHoveredUserID) {
			setHoveredUserID( oldID => oldID === userID ? undefined : oldID);
		}
	};

	const makeExtraStyles = useCallback((index)=>{
		return {
			right: 0,
			position: "absolute",
			top: totalRowHeight * index + (isXxl ? leaderboardStartRowYXxl : LeaderboardStartRowY),
			width: containerWidth
		};
	}, [totalRowHeight, containerWidth, leaderboardStartRowYXxl]);

	return (
		<div className={classes.leaderboard}>
			{theme.accessoryPanel.leaderboard.removeBackgroundSVG && 
				<div className={classes.titles}>
					<DisplayText variant={TextVariant.s20med} >
						{props.teamMode ? "Rank" : "Player"}
					</DisplayText>
					<DisplayText variant={TextVariant.s20med} >
						Score
					</DisplayText>
				</div>
			}
			<div className={classes.rowContainer} ref={containerRef} >
				{
					props.teamMode &&
					<>
						<LeaderboardTitleRow name={"Teams (avg. score)"} iconFilename={theme.accessoryPanel.leaderboard.removeBackgroundSVG ? "" : "team"} compact={compact} extraStyles={makeExtraStyles(0)}/>
						{
							props.teams.slice().sort((teamA, teamB)=>{
								const totalA = props.teamScores[teamA.key]?.total ?? 0;
								const totalB = props.teamScores[teamB.key]?.total ?? 0;
								return totalB - totalA;
							}).map((team, index) => {
								const extraStyles = makeExtraStyles(index+1);
								const teamInfo = theme.teamInfos[team.key];
								const total = props.teamScores[team.key]?.total ?? 0;
								return <LeaderboardRow borderColor={"black"} highlightColor={"black"} backgroundColor={teamInfo.darkBG} highlighted={true}
								                       rank={`${index+1}`} score={total} name={team.name}
								                       compact={compact} extraStyles={extraStyles}
								                       currentPlayer={false} hotStreak={false} rankChange={0}
								                       key={index+1} color={theme.accessoryPanel.leaderboard.teamAverage.color}
								/>;
							})
						}
						<LeaderboardTitleRow name={"Players"} iconFilename={theme.accessoryPanel.leaderboard.removeBackgroundSVG ? "" : "player"} compact={compact} extraStyles={makeExtraStyles(props.teams.length+1)}/>
					</>
				}
				{
					leaderboard.map((entry, index) => {
						const teamsIndex = teamRows + index;
						if(teamsIndex < rowLimit) {
							const extraStyles = makeExtraStyles(teamsIndex);
							const tied = lastPosition === entry.position ||
								(leaderboard.length > index+1 && entry.position === leaderboard[index+1].position);
							lastPosition = entry.position;
							const currentPlayer = index === userIndex;
							if(props.teamMode) {
								//Occasionally (when it's the current player) the team name is passed in via entry.team
								//rather than its key--the check below makes sure to extract the key (i.e. rather than Team One, it should be 'first').
								const teamInfo = theme.teamInfos[entry.teamID ?? "first"];

								return <LeaderboardRow extraStyles={extraStyles} key={teamsIndex}
								                       {...propsForEntry(theme, entry, tied, showAllRankChanges || currentPlayer, props.teamMode, currentPlayer )}
								                       onMouseEnter={()=>onMouseEnter(entry.userID)}
								                       onMouseLeave={()=>onMouseLeave(entry.userID)}
								                       currentPlayer={ currentPlayer } compact={compact}
								                       borderColor={"black"} backgroundColor={teamInfo.darkBG}
                                                       highlighted={false} highlightColor={"none"}/>;
							} else {
								return <LeaderboardRow extraStyles={extraStyles} key={teamsIndex}
								                       {...propsForEntry(theme, entry, tied, showAllRankChanges || currentPlayer, props.teamMode, currentPlayer )}
								                       onMouseEnter={()=>onMouseEnter(entry.userID)}
								                       onMouseLeave={()=>onMouseLeave(entry.userID)}
								                       currentPlayer={ currentPlayer } compact={compact}/>;
							}
						}
						return undefined;
					})
				}
				{ user && userRowAfter === true &&
					<LeaderboardRow backgroundColor={user.team && theme.teamInfos[user.team] ? theme.teamInfos[user.team].darkBG : theme.color.background.lighter}
									borderColor="" highlighted={false} rank={`${userState.position}`}
									score={userState.scores.total} name={user.nickname} currentPlayer={true}
									rankChange={userState.lastPosition ? userState.lastPosition - (userState.position ?? 0) : 0}
									hotStreak={userState.streak >= 3}
									compact={compact}
									extraStyles={{
										right: 0,
										position: "absolute",
										top: totalRowHeight * rowLimit + (isXxl ? leaderboardStartRowYXxl : LeaderboardStartRowY) + LeaderboardFinalRowGap,
										width: containerWidth
									}}
									onMouseEnter={()=>onMouseEnter(user.id)}
									onMouseLeave={()=>onMouseLeave(user.id)}
					/>
				}
			</div>
			{!theme.accessoryPanel.leaderboard.removeBackgroundSVG && <LeaderboardRowDivider className={classes.rowDivider} rows={rowLimit} compact={compact}/>}
		</div>
	);
}

export function propsForEntry(theme:Theme, entry:LeaderboardEntry, tied:boolean, showRank:boolean, teamMode?: boolean, currentPlayer?: boolean): LeaderboardRowProps {
	let props:any = {
		rank: `${tied?"T":""}${entry.position}`,
		highlighted: entry.position <= 3,
		score: entry.total,
		name: entry.nickname,
		hotStreak: entry.streak >= 3,
		rankChange: showRank && entry.lastPosition ? (entry.lastPosition ?? 0) - (entry.position ?? 0) : 0,
	};

	if(currentPlayer) {
		if (entry.position !== 0 && entry.position <= 3) {
			props.color = teamMode ? theme.accessoryPanel.leaderboard.row.currentPlayer.team.rankedColor : theme.accessoryPanel.leaderboard.row.currentPlayer.rankedColor;
		} else {
			props.color = teamMode ? theme.accessoryPanel.leaderboard.row.currentPlayer.team.unrankedColor : theme.accessoryPanel.leaderboard.row.currentPlayer.unrankedColor;
		}
	} else {
		if (entry.position !== 0 && entry.position <= 3) {
			props.color = teamMode ? theme.accessoryPanel.leaderboard.row.regularPlayer.team.rankedColor : theme.accessoryPanel.leaderboard.row.regularPlayer.rankedColor;
		} else {
			props.color = teamMode ? theme.accessoryPanel.leaderboard.row.regularPlayer.team.unrankedColor : theme.accessoryPanel.leaderboard.row.regularPlayer.unrankedColor;
		}
	}

	if(entry.position === 1) {
		props.backgroundColor = theme.accessoryPanel.leaderboard.firstPlace.backgroundColor;
		props.highlightColor = theme.accessoryPanel.leaderboard.firstPlace.highlightColor;
		props.borderColor = theme.accessoryPanel.leaderboard.firstPlace.borderColor;
	} else if(entry.position === 2) {
		props.backgroundColor = theme.accessoryPanel.leaderboard.secondPlace.backgroundColor;
		props.highlightColor = theme.accessoryPanel.leaderboard.secondPlace.highlightColor;
		props.borderColor = theme.accessoryPanel.leaderboard.secondPlace.borderColor;
	} else if(entry.position === 3) {
		props.backgroundColor = theme.accessoryPanel.leaderboard.thirdPlace.backgroundColor;
		props.highlightColor = theme.accessoryPanel.leaderboard.thirdPlace.highlightColor;
		props.borderColor = theme.accessoryPanel.leaderboard.thirdPlace.borderColor;
	} else {
		props.backgroundColor = theme.accessoryPanel.leaderboard.default.backgroundColor;
		props.borderColor = theme.accessoryPanel.leaderboard.default.borderColor;
	}
	return props;
}